import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface VideoState {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  hasEnded: boolean;
  bufferedTime: number;
}

export interface QuizState {
  currentQuestionIndex: number;
  totalQuestions: number;
  isLocked: boolean;
  isSubmitted: boolean;
  score?: number;
}

export interface SyncedTimestamp {
  time: number;
  questionIndex: number;
  action?: 'highlight' | 'unlock' | 'focus';
}

@Injectable({
  providedIn: 'root'
})
export class VideoQuizSyncService {
  // Video state
  private videoStateSubject = new BehaviorSubject<VideoState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    hasEnded: false,
    bufferedTime: 0
  });
  
  // Quiz state
  private quizStateSubject = new BehaviorSubject<QuizState>({
    currentQuestionIndex: 0,
    totalQuestions: 0,
    isLocked: true,
    isSubmitted: false
  });
  
  // Synced timestamps for questions
  private syncedTimestamps: SyncedTimestamp[] = [];
  
  // Observables
  videoState$: Observable<VideoState> = this.videoStateSubject.asObservable();
  quizState$: Observable<QuizState> = this.quizStateSubject.asObservable();
  
  constructor() {}
  
  // Video control methods
  updateVideoState(state: Partial<VideoState>): void {
    const currentState = this.videoStateSubject.value;
    this.videoStateSubject.next({ ...currentState, ...state });
    
    // Check for timestamp-based actions
    if (state.currentTime !== undefined) {
      this.checkTimestampActions(state.currentTime);
    }
    
    // Unlock quiz when video ends
    if (state.hasEnded) {
      this.unlockQuiz();
    }
  }
  
  // Quiz control methods
  updateQuizState(state: Partial<QuizState>): void {
    const currentState = this.quizStateSubject.value;
    this.quizStateSubject.next({ ...currentState, ...state });
  }
  
  // Synchronization methods
  setSyncedTimestamps(timestamps: SyncedTimestamp[]): void {
    this.syncedTimestamps = timestamps.sort((a, b) => a.time - b.time);
  }
  
  private checkTimestampActions(currentTime: number): void {
    // Find relevant timestamp within 2 second window
    const relevantTimestamp = this.syncedTimestamps.find(ts => 
      Math.abs(ts.time - currentTime) < 2
    );
    
    if (relevantTimestamp) {
      switch (relevantTimestamp.action) {
        case 'highlight':
          this.highlightQuestion(relevantTimestamp.questionIndex);
          break;
        case 'unlock':
          this.unlockQuestion(relevantTimestamp.questionIndex);
          break;
        case 'focus':
          this.focusQuestion(relevantTimestamp.questionIndex);
          break;
      }
    }
  }
  
  private highlightQuestion(index: number): void {
    this.updateQuizState({ currentQuestionIndex: index });
  }
  
  private unlockQuestion(index: number): void {
    // Implementation for partial quiz unlocking
    console.log(`Unlocking question ${index}`);
  }
  
  private focusQuestion(index: number): void {
    this.updateQuizState({ currentQuestionIndex: index });
  }
  
  unlockQuiz(): void {
    this.updateQuizState({ isLocked: false });
  }
  
  lockQuiz(): void {
    this.updateQuizState({ isLocked: true });
  }
  
  // Utility methods
  getVideoProgress(): number {
    const state = this.videoStateSubject.value;
    return state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;
  }
  
  isVideoComplete(requiredPercentage: number = 90): boolean {
    const state = this.videoStateSubject.value;
    return state.hasEnded || this.getVideoProgress() >= requiredPercentage;
  }
  
  reset(): void {
    this.videoStateSubject.next({
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      hasEnded: false,
      bufferedTime: 0
    });
    
    this.quizStateSubject.next({
      currentQuestionIndex: 0,
      totalQuestions: 0,
      isLocked: true,
      isSubmitted: false
    });
    
    this.syncedTimestamps = [];
  }
}